import { Fragment, FunctionComponent, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Appearance, ProductType } from '@/types';
import { getFaqUrl } from '@/utils/faq';
import { t } from '@/utils/i18n';
import { getDesignImageUrl } from '@/utils/imageUrls/getDesignImageUrl';
import { getProductTypeImageUrl } from '@/utils/imageUrls/getProductTypeImageUrl';
import { isCYOMode, isEmbroideryMode } from '@/utils/params';
import { useIsSmall } from '@/hooks/useMediaQuery';
import { getDefaultView } from '@/model/productType/getDefaultView';
import { getPrintArea } from '@/model/productType/getPrintArea';
import { getViewForPrintArea } from '@/model/productType/getViewForPrintArea';
import { selectDesign } from '@/redux/designs/getters/selectDesign';
import { Price } from '@/components/common/Price';
import { Drop } from '@/components/common/icons/Drop';
import { Embroidery } from '@/components/common/icons/Embroidery';
import { Info } from '@/components/common/icons/Info';
import { Text } from '@/components/common/icons/Text';
import { Tooltip } from '@/components/common/sidebar/Tooltip';
import './SizeSelectionPriceDetails.scss';

const commissionStrategyConfigFilter = (areas: PrintAreaPrice[]) => {
  const distinctDesigns = new Map<string, BreakdownConfiguration>();
  return areas.reduce<BreakdownConfiguration[]>((configs, area) => {
    area.configurations.forEach((config) => {
      if (!config.designId) {
        configs.push(config);
      } else {
        const distinctDesign = distinctDesigns.get(config.designId);
        if (distinctDesign) {
          if (distinctDesign.price < config.price) {
            distinctDesigns.set(config.designId, config);
            configs.splice(configs.indexOf(distinctDesign), 1, config);
          }
        } else {
          distinctDesigns.set(config.designId, config);
          configs.push(config);
        }
      }
    });

    return configs;
  }, []);
};

const printAreaName = (productType: ProductType, printAreaId: string) => {
  const printArea = getPrintArea(productType, printAreaId);
  if (printArea) {
    return getViewForPrintArea(productType, printArea).name;
  }
  return null;
};

const configurationName = (configuration: BreakdownConfiguration) => {
  if (configuration.designId) {
    return t('basket.designPrice');
  }
  if (configuration.text) {
    return `${t('basket.text')}: ${configuration.text}`;
  }
  return '';
};

interface DesignImageProps {
  designId: string;
}

const DesignImage: FunctionComponent<DesignImageProps> = ({ designId }) => {
  const design = useSelector(selectDesign(designId));
  return (
    <img
      className={classNames('price-details__configuration-image', {
        'price-details__configuration-image--light': Boolean(design.backgroundColor),
      })}
      src={getDesignImageUrl(designId, {
        width: 50,
        height: 50,
      })}
      alt=""
    />
  );
};

interface ConfigurationImageProps {
  configuration: BreakdownConfiguration;
}

const ConfigurationImage: FunctionComponent<ConfigurationImageProps> = ({ configuration }) => {
  if (configuration.designId) {
    return <DesignImage designId={configuration.designId} />;
  }

  if (configuration.icon) {
    return <img className="price-details__configuration-image" src={configuration.icon} alt="" />;
  }

  if (configuration.text) {
    return (
      <div className="price-details__configuration-image">
        <Text className="price-details__configuration-image" />
      </div>
    );
  }

  return null;
};

interface ProductTypeDetailProps {
  productType: ProductType;
  appearance: Appearance;
  priceBreakdown: PriceBreakdown;
}

const ProductTypeDetails: FunctionComponent<ProductTypeDetailProps> = ({
  productType,
  appearance,
  priceBreakdown,
}) => (
  <div className="price-details__section">
    <h3>{t('basket.product')}</h3>
    <div className="price-details__producttype">
      {productType && productType.defaultValues && (
        <img
          className="price-details__producttype-image"
          src={getProductTypeImageUrl(getDefaultView(productType), appearance.id, {
            width: 800,
            height: 800,
          })}
          alt={productType.name}
        />
      )}
      <span className="price-details__producttype-name">{`${productType.name}`}</span>
      <Price className="price-details__producttype-price" value={priceBreakdown.productType} />
    </div>
  </div>
);

interface PrintingCostItemProps {
  area: PrintAreaPrice;
  productType: ProductType;
}

const PrintingCostItem: FunctionComponent<PrintingCostItemProps> = ({ area, productType }) => {
  return (
    <div className="price-details__printing">
      {isEmbroideryMode() ? (
        <Embroidery className="price-details__printing-image" />
      ) : (
        <Drop className="price-details__printing-image" />
      )}
      <span className="price-details__printing-title">{printAreaName(productType, area.id)}</span>
      <Price
        className="price-details__printing-price"
        value={area.printing}
        zeroValue={t('basket.free')}
      />
    </div>
  );
};

interface PrintingCostProps {
  priceBreakdown: PriceBreakdown;
  productType: ProductType;
  showPrintingCostTooltip?: boolean;
}

const PrintingCosts: FunctionComponent<PrintingCostProps> = ({
  priceBreakdown,
  productType,
  showPrintingCostTooltip,
}) => {
  return (
    <div className="price-details__section">
      <div className="price-details__section-header price-details__printing-header">
        <h3>
          {t(isEmbroideryMode() ? 'embroidery.labels.printingCosts' : 'basket.printingCosts')}
        </h3>
        {showPrintingCostTooltip && (
          <>
            <Info width="1.5em" height="1.5em" />
            <Tooltip>
              {isEmbroideryMode() ? (
                <p>{t('embroidery.printingCosts.overlap')}</p>
              ) : (
                <>
                  <p>{t('basket.printingCosts.overlap')}</p>
                  <a
                    href={getFaqUrl('pricePerPrintArea')}
                    className="tooltip__link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('basket.learnMore')}
                  </a>
                </>
              )}
            </Tooltip>
          </>
        )}
      </div>
      {priceBreakdown.areas.map((area) => (
        <PrintingCostItem key={area.id} area={area} productType={productType} />
      ))}
    </div>
  );
};

interface ConfigurationCostItemProps {
  config: BreakdownConfiguration;
}

const ConfigurationCostItem: FunctionComponent<ConfigurationCostItemProps> = ({ config }) => {
  return (
    <div key={config.id} className="price-details__configuration">
      <ConfigurationImage configuration={config} />
      <span className="price-details__configuration-title">{configurationName(config)}</span>
      <Price
        className="price-details__configuration-price"
        value={config.price}
        zeroValue={t('basket.free')}
      />
    </div>
  );
};

interface ConfigurationCostProps {
  priceBreakdown: PriceBreakdown;
  showPrintingCostTooltip?: boolean;
  specialFontCommission?: number | null;
}

const ConfigurationCosts: FunctionComponent<ConfigurationCostProps> = ({
  priceBreakdown,
  showPrintingCostTooltip,
  specialFontCommission,
}) => {
  const configurations = commissionStrategyConfigFilter(priceBreakdown.areas);
  const [showTooltip, setShowTooltip] = useState(false);
  const isSmall = useIsSmall();

  return (
    <div className="price-details__section">
      <div className="price-details__section-header price-details__printing-header">
        <h3>{t('pricedetails.designprices')}</h3>
        {showPrintingCostTooltip && isCYOMode() && isSmall && specialFontCommission ? (
          <button
            className="price-details__section--tooltip-btn"
            onClick={() => setShowTooltip(!showTooltip)}
          >
            <Info width="1.5em" height="1.5em" />
            {showTooltip && (
              <Tooltip>
                <p>{t('priceDetails.surchargeTooltip')}</p>
              </Tooltip>
            )}
          </button>
        ) : isCYOMode() && specialFontCommission ? (
          <>
            <Info width="1.5em" height="1.5em" />
            <Tooltip>
              <p>{t('priceDetails.surchargeTooltip')}</p>
            </Tooltip>
          </>
        ) : null}
      </div>
      {configurations.map((config) => (
        <ConfigurationCostItem config={config} key={config.id} />
      ))}
    </div>
  );
};

interface TotalProps {
  discountBreakdown: DiscountBreakdown;
  totalItemCount: number;
}

const Totals: FunctionComponent<TotalProps> = ({ discountBreakdown, totalItemCount }) => {
  const { currentDiscount, product, reducedProduct } = discountBreakdown;
  return (
    <div className="price-details__totals">
      {currentDiscount && reducedProduct && (
        <Fragment>
          <div className="price-details__totals-line">
            <span>{t('basket.totalCosts')}</span>
            <Price value={product} />
          </div>
          <div className="price-details__totals-line price-details__totals-line--discounted">
            <span>
              {t('basket.discountNow', currentDiscount.toFixed(0), totalItemCount.toFixed(0))}
            </span>
            <span>
              <Price value={reducedProduct - product} />
            </span>
          </div>
        </Fragment>
      )}
      <div className="price-details__totals-line price-details__totals-line--sum">
        <span>{t('basket.itemPrice')}</span>
        <span>
          <Price value={discountBreakdown.reducedProduct || discountBreakdown.product} />
        </span>
      </div>
    </div>
  );
};

interface SizeSelectionPriceDetailsProps {
  priceBreakdown: PriceBreakdown;
  discountBreakdown: DiscountBreakdown;
  totalItemCount: number;
  productType: ProductType;
  appearance: Appearance;
  showPrintingCostTooltip?: boolean;
  specialFontCommission?: number | null;
}

export const SizeSelectionPriceDetails: FunctionComponent<SizeSelectionPriceDetailsProps> = ({
  priceBreakdown,
  discountBreakdown,
  totalItemCount,
  productType,
  appearance,
  showPrintingCostTooltip = true,
  specialFontCommission,
}) => (
  <div className="price-details">
    <ProductTypeDetails
      productType={productType}
      appearance={appearance}
      priceBreakdown={priceBreakdown}
    />
    {priceBreakdown.areas && priceBreakdown.areas.length > 0 && (
      <>
        <PrintingCosts
          priceBreakdown={priceBreakdown}
          productType={productType}
          showPrintingCostTooltip={showPrintingCostTooltip}
        />
        <ConfigurationCosts
          showPrintingCostTooltip={showPrintingCostTooltip}
          priceBreakdown={priceBreakdown}
          specialFontCommission={specialFontCommission}
        />
      </>
    )}
    <Totals discountBreakdown={discountBreakdown} totalItemCount={totalItemCount} />
  </div>
);
